<template>
  <div>
    <div class="breadcrumbs mb-2">
      <v-breadcrumbs divider=">" :items="breadcrumbsList">
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            :to="{ name: item.path }"
            :disabled="item.disabled"
            exact
          >
            {{ item.text }}
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
    </div>
    <PageTitle :pageHeading="'Trash'" :rightsideButton="true">
      <v-btn
        v-if="
          ticketList.length !== 0 &&
          $can('create','tickets')
        "
        outlined
        :disabled="selectedTicketsData.length === 0"
        class="font-weight-bold px-2"
        v-track="'restore-trash-tickets'"
        @click="onRestore()"
      >
        <span class="pl-2 pr-3"> Restore </span>
      </v-btn>
    </PageTitle>
    <div
      v-if="ticketList.length === 0 && !loading"
      class="title d-flex flex-column justify-center align-center mt-12"
    >
      <img
        src="@/assets/img/no-data.svg"
        class="mb-2"
      />
      <p class="body-1">Looks like there are no Tickets in Trash.</p>
    </div>
    <!----- ticket list page table ---->
    <v-data-table
      v-else
      id="ticket-list-table"
      :show-select="$can('create','tickets')"
      :headers="headers"
      :items="ticketList"
      :options.sync="pagination"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 25, 50, 75, 100],
      }"
      :mobile-breakpoint="960"
      :items-per-page="perPage"
      :loading="loading"
      item-key="id"
      class="elevation-2 mt-5 ticket-list-table"
      @input="getSelectData"
      checkbox-color="#45B6F8"
      :value="selectedTicketsData"
      :item-class="rowClass"
    >
      <template v-slot:[`item.id`]="{ item }">
        <div class="d-flex">
          <router-link
            v-track="'open-ticket-details'"
            :to="{ name: 'ticket-details', params: { id: item.id } }"
          >
            #{{ item.id }}
          </router-link>
        </div>
      </template>

      <template v-slot:[`item.contact_name`]="{ item }">
        <router-link
          v-track="'contact-dashboard'"
          :to="{
            name: 'contact-dashboard',
            params: { accountId: account.id, contactId: item.contact_id },
          }"
        >
          {{ item.contact_name ? item.contact_name : '--'  | upperCaseFirst }}
        </router-link>
      </template>

      <template v-slot:[`item.subject`]="{ item }">
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <router-link
              v-track="'ticket-details'"
              :to="{ name: 'ticket-details', params: { id: item.id } }"
              class="body-1 word-break text-ellipsis cursor-pointer"
            >
              <span v-on="on"
                >{{ item.subject | upperCaseFirst }}
              </span></router-link
            >
          </template>
          <span class="word-break">{{ item.subject | upperCaseFirst }}</span>
        </v-tooltip>
      </template>

      <template v-slot:[`item.tags`]="{ item }">
        <div
          class="menu-container tags"
          v-if="'tags' in item && item.tags.length > 0"
        >
          <v-chip
            v-for="(tags, index) in item.tags ? item.tags.slice(0, 1) : []"
            :key="index"
            class="ma-1"
            color="rgb(208, 218, 232)"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ getModified(tags.name, 7) }}
                </span>
              </template>
              <span>{{ tags.name }}</span>
            </v-tooltip>
          </v-chip>
          <v-menu offset-y attach :top="enableTop(item.id)">
            <template v-slot:activator="{ on, attrs }">
              <a
                v-if="item.tags.length > 1"
                class="ma-1"
                v-track="'load-more-tags'"
                v-bind="attrs"
                v-on="on"
              >
                +{{ item.tags.length - 1 }} more
              </a>
            </template>

            <v-list class="menu-list">
              <v-list-item-group>
                <v-list-item
                  v-for="(tags, index) in item.tags.slice(1)"
                  :key="index"
                >
                  {{ getModified(tags.name, 10) }}
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-menu>
        </div>
      </template>

      <template v-slot:[`item.deleted_by`]="{ item }">
        {{ item.deleted_by }}
      </template>

      <template v-slot:[`item.deleted_at`]="{ item }">
        {{ item.deleted_at | formatDate }}
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        {{ item.created_at | formatDate }}
      </template>
    </v-data-table>
    <!-- Restore selected ticket dialog -->
    <dialog-box v-bind="dialog.attrs" v-on="dialog.on" />
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle.vue";
import { $trashedTickets, $tickets } from "@/services/Index";
import commonFunctions from "@/commonFunctions";
import { mapGetters } from "vuex";
import DialogBox from "@core/components/ui/DialogBox";

export default {
  name: "TrashTickets",
  components: {
    PageTitle,
    DialogBox,
  },
  data() {
    return {
      breadcrumbsList: [
        {
          text: "All Tickets",
          disabled: false,
          path: "tickets",
        },
        {
          text: "Trash",
          disabled: true,
          path: "",
        },
      ],
      headers: [
        {
          text: "Ticket#",
          align: "start",
          sortable: false,
          value: "id",
          width: "50px",
          show: true,
        },
        {
          text: "Contact Name",
          value: "contact_name",
          width: "200px",
          show: true,
          disabled: true,
        },
        {
          text: "Subject",
          value: "subject",
          class: "subject-col",
          show: true,
          width: "250px",
          disabled: true,
        },

        {
          text: "Tags",
          sortable: false,
          value: "tags",
          width: "200px",
          show: false,
        },
        {
          text: "Deleted By",
          value: "deleted_by",
          width: "150px",
          show: true,
        },
        {
          text: "Deleted At",
          value: "deleted_at",
          width: "220px",
          show: true,
        },
        {
          text: "Date Created",
          value: "created_at",
          width: "220px",
          show: true,
        },
      ],
      ticketList: [],
      perPage: 10,
      page: 1,
      totalItems: 0,
      pagination: {},
      loading: false,
      selectedTicketsData: [],
      dialog: {
        attrs: {
          show: false,
          title: "Restore Ticket",
          loading: false,
          confirmBtn: true,
          confirmationText: "Restore",
        },
        on: {
          confirm: this.onConfirm,
          cancel: () => {
            this.dialog.attrs.show = false;
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      account: ["auth/account"],
    }),
  },
  mounted() {},
  watch: {
    pagination: {
      handler(newVal, oldVal) {
        /** prevent this check to invoked function on page load */
        if (oldVal.itemsPerPage) {
          this.getTicketList();
        }
      },
      deep: true,
    },

    /*** temporary code for global search */
    $route: {
      handler: function (to, from) {
        this.searchString = this.$route.query.name
          ? this.$route.query.name
          : "";
        this.pagination.page = 1;
        this.getTicketList();
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    /**
     * @Description
     * Function to retrieve trashed tickets from the server, handling pagination, sorting, and search criteria.
     */
    getTicketList() {
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const query = {
        q: {
          subject_or_id_cont: this.searchString,
          s: "deleted_at desc",
        },
      };
      const payload = {
        page,
        per_page: itemsPerPage,
        q: { ...query.q },
      };

      if (sortBy && sortBy.length) {
        // If sorting ..
        const dir = sortDesc.length
          ? sortDesc[0] === true
            ? "desc"
            : "asc"
          : "";

        payload.q.s = `${
          sortBy[0] == "contact_name"
            ? "contact_first_name"
            : sortBy[0] == "deleted_by"
            ? "agent_first_name"
            : sortBy[0]
        } ${dir}`;
      }
      $trashedTickets
        .fetchList(payload, {})
        .then((response) => {
          this.totalItems = response.data.pagination.total_count;
          this.ticketList = response.data.tickets;
        })
        .finally(() => {
          this.selectedTicketsData = [];
          this.loading = false;
        });
    },
    /**
     * @Description
     * get selected ticket data
     */
    getSelectData(data) {
      this.selectedTicketsData = data;
    },
    /**
     * @Description
     * Distinguish the closed ticket row.
     */
    rowClass(item) {
      let rowClass = "table-row";
      if (item.status == "closed") {
        rowClass = "closed-row";
      }
      return rowClass;
    },
    enableTop(id) {
      return commonFunctions.calculateTop(id, this.ticketList);
    },
    /**
     * @Description
     * Modify the tag name using substring.
     */
    getModified(tagName, maxLength) {
      return tagName.length > maxLength
        ? tagName.substring(0, maxLength) + "…"
        : tagName;
    },
    /**
     * @Description
     * Display the restore confirmation modal box before actually restoring the ticket.
     */
    onRestore() {
      this.dialog.attrs.subtitle = `Are you sure you want to restore the selected ticket${ this.selectedTicketsData.length > 1 ? "s" : "" }?`;
      this.dialog.attrs.show = true;
    },
    /**
     * @description
     * Handles the confirmation of ticket restore by first initiating the restore process,
     * indicating loading status, and then closing the dialog box.
     */
    onConfirm() {
      if (this.selectedTicketsData.length !== 0) {
        this.dialog.attrs.loading = true;

        // Manage payload for tickets restore API Call
        const payload = {
          ids: this.selectedTicketsData.map(ticket => ticket.id)
        }

        $tickets.restore(payload, {}).then((response) => {
          this.$store.dispatch("ui/snackbar", [{
            message: `Ticket${this.selectedTicketsData.length > 1 ? "s" : ""} restored successfully.`,
            type: "success",
          }]);
          this.pagination.page = 1;
          this.getTicketList();
        }).catch((error) => {
          this.$store.dispatch("ui/snackbar", [
            {
              message: this.$errors(error.response.data).msg({
                capitalize: false,
                prepend: `${this.$errors(
                  error.response.data
                ).subjectText()} `,
              }),
              type: "error",
            },
          ]);
        }).finally(() => {
          this.dialog.attrs.loading = false;
          this.dialog.attrs.show = false;
        })
      }
    },
  },
};
</script>
